/* eslint-disable react/react-in-jsx-scope */
import React from 'react';
import { CheckBoxOutlined, IndeterminateCheckBoxOutlined } from '@mui/icons-material';
import _ from 'lodash';
import i18next from 'i18next';
import { CONSTANTS, CUSTOM_PATH } from 'generic/core/constants';
import { getContrastRatio, lighten } from '@mui/material';

// Site intéressant pour choisir des couleurs "associables" :
// https://www.canva.com/colors/color-wheel/

/* ------------ Complémentaires ------------ */
// Bleu et orange classique
// const primary = '#407ec9';
// const secondary = '#f3681f';

// Violet et jaune
// const primary = '#3d3d6b';
// const secondary = '#fdc431';

// Turquoise orange
// const primary = '#2a7b9b';
// const secondary = '#f58c2d';

// Bleu saumon pastel
// const primary = '#3a97a9';
// const secondary = '#cd7e6f';

/* ------------ Analogues ------------ */
// Bleu et Violet
// const primary = '#2E4A62';
// const secondary = '#4E6B89';

const {
  primary,
  secondary,
  primaryDark,
  secondaryDark,
  outlinedBorderColor,
  outlinedBorderColorDark,
  background,
  backgroundSpecial,
  backgroundDark,
  backgroundDarkSpecial,
  backgroundHeaderDark,
  forcedBackgroundHeader,
} = CONSTANTS.APP_COLORS;

const backgroundHeader = forcedBackgroundHeader || primary;
const backgroundLogin = primary;
const backgroundLoginDark = backgroundDark;
const spacing = 8;

let THEME = {
  // LIGHT MODE
  LIGHT: {
    THEME: {
      typography: {
        fontFamily: 'Inter',
      },
      components: {
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: '64px',
              '& .MuiMenuItem-root': {
                borderRadius: '5px',
              },
            },
          },
        },
        MuiTextField: {
          defaultProps: {
            variant: 'outlined',
            margin: 'dense',
            size: 'small',
            autoComplete: 'off',
          },
        },
        MuiSelect: {
          defaultProps: {
            variant: 'outlined',
          },
        },
        MuiSvgIcon: {
          variants: [
            {
              props: { fontSize: 'extraSmall' },
              style: {
                fontSize: '16px',
              },
            },
          ],
        },
        // style des Icônes des ListItem
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              minWidth: 'auto',
              marginRight: '10px',
              minHeight: '22px',
            },
          },
        },
        // style des Icônes des ListItem dans le contexte
        // d'un menu
        MuiMenuItem: {
          styleOverrides: {
            root: {
              '& .MuiListItemIcon-root': {
                minWidth: 'auto',
                marginRight: '10px',
              },
            },
          },
        },
        // style des "toasts"/tooltips
        MuiTooltip: {
          defaultProps: {
            arrow: true,
          },
          styleOverrides: {
            tooltip: {
              fontSize: '1em',
              maxWidth: '600px',
            },
          },
        },
        // style des cards
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
            },
          },
        },
        MuiCardActionArea: {
          styleOverrides: {
            root: {
              backgroundColor: '#F8FAFC',
            },
          },
        },
        MuiCardHeader: {
          styleOverrides: {
            title: {
              fontWeight: 'bold',
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            scrollButtons: {
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              minHeight: '48px',
            },
          },
        },
        // style des labels des inputs
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: '#333333',
              fontSize: '1.1em',
            },
          },
        },
        // style des labels des autres inputs (checkbox, switch...)
        MuiFormControl: {
          styleOverrides: {
            root: {
              width: '100%',
              marginBottom: 0,
              '&:not(.forcedClassicFieldsetField)': {
                '&:has(> label)': {
                  marginTop: '35px',
                },
                '& fieldset.MuiOutlinedInput-notchedOutline': {
                  top: '0px',
                  '& > legend': {
                    display: 'none',
                  },
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(0px, -28px)',
                },
                '&.formFieldset:has(> legend) > .MuiInputLabel-root': {
                  transform: 'translate(0px, -48px)',
                },
                '& .MuiInputLabel-root, &.formFieldset:has(> legend) > .MuiInputLabel-root': {
                  fontSize: '14px',
                  fontWeight: '500',
                },
              },
            },
          },
        },
        MuiInputLabel: {
          defaultProps: {
            shrink: true,
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              '& .MuiIconButton-root': {
                padding: '4px',
              },
            },
          },
        },
        // style des inputs outlined
        MuiOutlinedInput: {
          defaultProps: {
            notched: true,
            size: 'small',
          },
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: outlinedBorderColor,
                '& legend': {
                  fontSize: '0.9em',
                },
              },
            },
          },
        },
        // style des checkbox
        MuiCheckbox: {
          defaultProps: {
            // eslint-disable-next-line react/jsx-filename-extension
            checkedIcon: <CheckBoxOutlined />,
            indeterminateIcon: <IndeterminateCheckBoxOutlined />,
          },
          styleOverrides: {
            root: {
              margin: '0px 0px 3px 10px',
              padding: '5px',
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              backgroundColor: '#F8FAFC',
            },
          },
        },
        MUIDataTable: {
          styleOverrides: {
            paper: {
              '@media (min-width: 1200px)': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              },
            },
            responsiveBase: {
              flexGrow: '1',
            },
          },
        },
        MUIDataTableHeadRow: {
          styleOverrides: {
            root: {
              position: 'relative',
              zIndex: 1300,
            },
          },
        },
        MUIDataTableToolbar: {
          styleOverrides: {
            root: {
              flexShrink: 0,
              '@media (max-width: 1200px)': {
                position: 'sticky',
                top: 0,
                backgroundColor: background,
                zIndex: 1300,
              },
            },
            actions: {
              flex: '0',
              display: 'flex',
            },
          },
        },
        MUIDataTableToolbarSelect: {
          styleOverrides: {
            root: {
              flex: '0',
              minHeight: '64px',
              padding: '0',
            },
          },
        },
        MUIDataTableFooter: {
          styleOverrides: {
            root: {
              '@media (max-width: 1200px)': {
                position: 'sticky',
                bottom: 0,
                backgroundColor: background,
                zIndex: 1100,
              },
            },
          },
        },
        MuiTypography: {
          variants: [
            {
              props: { variant: 'h6' },
              style: {
                fontSize: '1rem',
                lineHeight: 'initial',
              },
            },
          ],
        },
        // Style des boutons
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              '&.MuiButtonGroup-groupedOutlined.Mui-disabled': {
                border: '1px solid rgba(0, 0, 0, 0.26)',
              },
              '&.MuiButtonGroup-grouped:not(:last-of-type):not(.Mui-disabled):not(.MuiButton-outlined)': {
                borderColor: 'white',
              },
            },
            endIcon: {
              margin: '0 -6px 0 4px',
            },
          },
          defaultProps: {
            variant: 'contained',
            color: 'primary',
            disableElevation: true,
          },
          variants: [
            {
              props: { size: 'extraSmall' },
              style: {
                padding: '2px 8px',
                '& svg': {
                  fontSize: '18px',
                },
              },
            },
          ],
        },
        MuiLoadingButton: {
          defaultProps: {
            variant: 'contained',
            color: 'primary',
            disableElevation: true,
          },
        },
        MuiFab: {
          styleOverrides: {
            root: {
              boxShadow: `0px 3px 5px -1px rgb(255 255 255 / 52%),
                0px 6px 10px 0px rgb(255 255 255 / 33%),
                0px 1px 18px 0px rgb(255 255 255 / 52%)`,
              '&:active': {
                boxShadow: `0px 3px 5px -1px rgb(255 255 255 / 52%),
                0px 6px 10px 0px rgb(255 255 255 / 33%),
                0px 1px 18px 0px rgb(255 255 255 / 52%)`,
              },
            },
          },
          variants: [
            {
              props: { size: 'extraSmall' },
              style: {
                height: '28px',
                minHeight: 'auto',
                width: '28px',
                '& svg': {
                  fontSize: '20px',
                },
              },
            },
          ],
        },
        MuiLink: {
          styleOverrides: {
            root: {
              color: '#0782C1',
            },
          },
        },
        // Style des chips
        MuiChip: {
          styleOverrides: {
            root: {
              height: 'auto',
              minHeight: '32px',
            },
            label: {
              whiteSpace: 'normal',
            },
          },
          variants: [
            {
              props: { variant: 'filledPrimary' },
              style: {
                backgroundColor: `${primary}20`,
              },
            },
            {
              props: { variant: 'filledSecondary' },
              style: {
                backgroundColor: `${secondary}20`,
              },
            },
            {
              props: { size: 'extraSmall' },
              style: {
                minHeight: '20px',
                '& .MuiChip-icon': {
                  marginLeft: '4px',
                  marginRight: '-4px',
                  fontSize: '14px',
                },
                '& .MuiChip-label': {
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px',
                },
              },
            },
          ],
        },
      },
      palette: {
        mode: 'light',
        primary: {
          main: primary,
          transparent: `${primary}20`,
          attenuated: `${primary}80`,
        },
        header: {
          main: backgroundHeader,
          menuSelected: primary,
        },
        backgroundLogin: {
          main: backgroundLogin,
          light: lighten(backgroundLogin, 0.2),
          contrastText: getContrastRatio(backgroundLogin, '#fff') > 3 ? '#fff' : '#000',
        },
        text: {
          primary: '#334155',
          neutral: 'rgba(0, 0, 0, 0.54)',
          disabled: 'rgba(0, 0, 0, 0.38)',
          error: '#d32f2f',
          success: '#4caf50',
        },
        secondary: {
          main: secondary,
        },
        background: {
          paper: background,
          default: background,
          special: backgroundSpecial,
        },
        border: {
          main: outlinedBorderColor,
        },
        // Couleurs utiles aux éléments associés aux réseaux sociaux
        dailymotion: {
          main: '#0166cc',
        },
        facebook: {
          main: '#1877f2',
        },
        linkedin: {
          main: '#0a66c2',
        },
        twitter: {
          main: '#55acee',
        },
        whatsapp: {
          main: '#3fc252',
        },
        youtube: {
          main: '#ef462c',
        },
      },
      // Valeur a modifier pour "aérer" ou "densifier" l'interface
      // si la valeur est 8 (par exemple), l'unité de "spacing" sera
      // de 8px (donc theme.spacing(2) vaudra 2*8px soit 16px)
      spacing,
      custom: {
        // style du logo dans le Header
        logo: {
          height: '38px',
          marginRight: '8px',
        },
        // style des mots highlités dans le search
        highlightKeyword: {
          fontWeight: 'bold',
          color: primary,
        },
      },
    },
    GLOBAL_STYLES: {
      ':root': {
        '--ck-color-toolbar-background': '#fff',
        '--ck-color-list-background': '#fff',
        '--ck-color-dropdown-panel-background': '#fff',
        '--ck-color-list-button-on-text': '#fff',
        '--ck-color-base-text': '#333',
        '--ck-color-button-on-color': '#2977ff',
        '--ck-color-button-on-background': '#f0f7ff',
        '--ck-color-button-default-hover-background': '#f0f0f0',
        '--ck-color-button-default-active-background': '#f0f0f0',
        '--ck-color-button-on-hover-background': '#dbecff',
        '--ck-color-button-on-active-background': '#dbecff',
      },
      '.highcharts-map-navigation .highcharts-button-symbol': {
        strokeWidth: '2px',
      },
      '.highcharts-menu': {
        padding: '0!important', // beurk mais pas trouvé d'alternative...
      },
      '.react-grid-item > .react-resizable-handle::after': {
        borderColor: 'rgba(0, 0, 0, 0.4);',
      },
      '.react-grid-item.react-draggable-dragging .MuiPaper-root, .react-grid-item.resizing .MuiPaper-root': {
        border: '1px solid #c5c5c5',
      },
      '.react-grid-item.react-resizable.resizing': {
        zIndex: 3,
      },
      '.react-grid-item.react-grid-placeholder.placeholder-resizing': {
        zIndex: 4,
      },
      '.dashboard-footer': {
        backgroundColor: '#F8FAFC',
      },
      // bordure autour des fieldsets des formulaires
      '.formFieldset': {
        borderColor: outlinedBorderColor,
        marginTop: '20px',
        borderRadius: '5px',
        borderStyle: 'solid',
        borderWidth: '1px',
        boxSizing: 'border-box',
        // override du style des legends des fieldsets
        '& > legend': {
          marginLeft: '8px',
          paddingRight: '5px',
          paddingLeft: '5px',
          fontSize: '0.9em',
          '& .MuiFormControlLabel-root': {
            '& .MuiFormControlLabel-label': {
              fontSize: '1em',
            },
          },
        },
      },
      '[data-rmiz-modal-overlay="visible"]': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      },
      blockquote: {
        borderLeft: '5px solid #ccc',
        fontStyle: 'italic',
        marginLeft: '0',
        marginRight: '0',
        overflow: 'hidden',
        paddingLeft: '1.5em',
        paddingRight: '1.5em',
      },
      '.stickyHeader': {
        position: 'sticky',
        top: '0',
        backgroundColor: background,
      },
      '.withBackgroundSpecial': {
        backgroundColor: backgroundSpecial,
      },
      '.bottomBtnsContainer': {
        padding: '8px',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: background,
        position: 'sticky',
        zIndex: '1200',
        bottom: '0',
      },
      '#add-to-item-datatable .MuiToolbar-root': {
        '& .MuiFormControl-fullWidth.MuiTextField-root': {
          flex: '1 0',
        },
        '& .MuiButtonBase-root': {
          display: 'none',
        },
      },
      // styles des snackbar notistack
      '.notistack-MuiContent': {
        flexWrap: 'nowrap',
      },
      '#notistack-snackbar': {
        '@media (min-width: 600px)': {
          maxWidth: '400px',
        },
      },
      '.notistack-MuiContent .MuiButton-root': {
        color: 'white',
        minWidth: 'auto',
      },
      // style des inputs de date ayant cette classe
      '.datePickerInput': {
        width: '200px',
        paddingRight: '0',
      },
      'input[readonly]': {
        color: 'rgba(0, 0, 0, 0.38)',
        cursor: 'default',
      },
      '.inputLabelWithHelpIcon': {
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        '& .MuiSvgIcon-root': {
          marginLeft: '8px',
        },
      },
      '.yellowStar': {
        color: '#fdd835',
        '& path': {
          stroke: '#757575',
        },
      },
      '.leftColumnAnRBtn': {
        width: '100%',
        borderRadius: 'unset',
      },
      '.height100': {
        height: '100%',
      },
      '.overflowAuto': {
        overflow: 'auto',
      },
      '.desktopOnlyOverflow': {
        '@media (min-width: 1200px)': {
          overflow: 'auto',
        },
      },
      '.padding0': {
        padding: '0',
      },
      '.width100': {
        width: '100%',
      },
      '.minWidthAuto': {
        minWidth: 'auto',
      },
      '.flexShrink0': {
        flexShrink: 0,
      },
      '.flexGrow1': {
        flexGrow: 1,
      },
      '.flexDirectionColumn': {
        flexDirection: 'column',
      },
      '.displayBlock': {
        display: 'block',
      },
      '.displayFlex': {
        display: 'flex',
      },
      '.alignItemsCenter': {
        alignItems: 'center',
      },
      '.justifyContentSpaceBetween': {
        justifyContent: 'space-between',
      },
      '.cursorPointer': {
        cursor: 'pointer',
      },
      '.positionRelative': {
        position: 'relative',
      },
      '.react-transform-wrapper': {
        width: '100%!important',
        '& .react-transform-component': {
          width: '100%',
          '& > div': {
            width: '100%',
          },
        },
      },
      // Style des tags dans le html des documents
      // Sélecteur '.html-with-tags > .html-tag' pour n'afficher que les annotations les plus "hautes"
      // Sélecteur '.html-tag:not(:has(span))' pour n'afficher que les annotations les plus "profondes"
      '.html-with-tags > .html-tag': {
        backgroundColor: 'rgb(211, 211, 211)',
        border: '1px solid #ccc',
        borderRadius: '2px',
        padding: '3px',
        '&::after': {
          verticalAlign: 'super',
          fontSize: '80%',
          paddingLeft: '3px',
          content: 'attr(type-text)',
        },
        '&.Concept, &.Coldconcept': {
          backgroundColor: '#2196F320',
        },
        '&.Product': {
          backgroundColor: '#E91E6320',
        },
        '&.Person': {
          backgroundColor: '#6a66cc20',
        },
        '&.Location': {
          backgroundColor: '#cb5d9e20',
        },
        '&.Company': {
          backgroundColor: '#7893f520',
        },
        '&.Media': {
          backgroundColor: '#b3781b20',
        },
        '&.Organization': {
          backgroundColor: '#2f590a20',
        },
        '&.Organonoff': {
          backgroundColor: '#4CAF5020',
        },
        '&.Event': {
          backgroundColor: '#FFC10720',
        },
      },
    },
    HIGHCHARTS: {
      // useful palettes website : https://coolors.co/palettes/popular/orange,gradient
      colors: [
        '#264653',
        '#287271',
        '#2A9D8F',
        '#8AB17D',
        '#BABB74',
        '#E9C46A',
        '#EFB366',
        '#F4A261',
        '#EE8959',
        '#E76F51',
      ],
      speakersColors: [
        '#31A6F3',
        '#F59C9C',
        '#2C5F5B',
        '#D56732',
        '#0496A2',
        '#B41A84',
        '#046084',
        '#E0D53B',
        '#783B09',
        '#7F62B5',
      ],
      entitiesColors: [
        '#31A6F3',
        '#F19C9C',
        '#5CBF50',
        '#FF6752',
        '#049698',
        '#B41A59',
        '#046084',
        '#E0D53B',
        '#FF9737',
        '#7F62B5',
      ],
      relationsEntitiesColors: [
        '#006064',
        '#E91E63',
        '#ff7777',
        '#2196F3',
        '#52a5af',
        '#6a66cc',
        '#cb5d9e',
        '#4CAF50',
        '#FFC107',
        '#7893f5',
        '#b3781b',
        '#2f590a',
        '#ff0000',
        '#00ff00',
        '#0000ff',
      ],
      chart: {
        ignoreHiddenSeries: true,
        backgroundColor: background,
        style: {
          color: '#000000',
          fontFamily: 'Inter',
          fontWeight: 'normal',
        },
      },
      title: {
        style: {
          fontFamily: 'Inter',
          fontWeight: 'normal',
          fontSize: '18px',
          color: '#333333',
        },
      },
      subtitle: {
        style: {
          fontFamily: 'Inter',
          fontWeight: 'normal',
        },
      },
      legend: {
        align: 'right',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#333333',
        },
        itemHoverStyle: {
          color: '#000000',
        },
      },
      xAxis: {
        gridLineWidth: 1,
        gridLineColor: '#F3F3F3',
        lineColor: '#F3F3F3',
        minorGridLineColor: '#F3F3F3',
        tickColor: '#F3F3F3',
        tickWidth: 1,
        labels: {
          style: {
            color: '#666666',
          },
        },
      },
      yAxis: {
        gridLineColor: '#F3F3F3',
        lineColor: '#F3F3F3',
        minorGridLineColor: '#F3F3F3',
        tickColor: '#F3F3F3',
        tickWidth: 1,
        labels: {
          style: {
            color: '#666666',
          },
        },
      },
      mapNavigation: {
        buttonOptions: {
          theme: {
            fill: '#f7f7f7',
            stroke: '#cccccc',
            style: {
              color: '#333333',
            },
            states: {
              hover: {
                fill: '#e6e6e6',
              },
            },
          },
        },
      },
      navigation: {
        menuStyle: {
          background,
        },
        menuItemStyle: {
          color: '#000000',
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            symbolStroke: '#666666',
            theme: {
              fill: background,
              stroke: 'none',
              states: {
                hover: {
                  fill: '#e6e6e6',
                },
                select: {
                  fill: '#e6e6e6',
                },
              },
            },
          },
        },
      },
      plotOptions: {
        area: {
          lineWidth: 2,
        },
        line: {
          lineWidth: 2,
        },
        spline: {
          lineWidth: 2,
        },
        pie: {
          borderRadius: 0,
          dataLabels: {
            style: {
              fontWeight: '600',
            },
            color: '#000000',
            connectorColor: '#C5c5C5',
            connectorShape: 'fixedOffset',
          },
        },
        column: {
          borderWidth: 1,
          borderRadius: 0,
        },
        bar: {
          borderWidth: 1,
        },
        sunburst: {
          borderWidth: 1,
        },
        networkgraph: {
          dataLabels: {
            style: {
              fontWeight: '600',
            },
          },
        },
        treemap: {
          borderWidth: 1,
          dataLabels: {
            style: {
              fontWeight: '600',
            },
          },
        },
      },
      lang: {
        months: [
          i18next.t('commons.months.january'),
          i18next.t('commons.months.february'),
          i18next.t('commons.months.march'),
          i18next.t('commons.months.april'),
          i18next.t('commons.months.may'),
          i18next.t('commons.months.june'),
          i18next.t('commons.months.july'),
          i18next.t('commons.months.august'),
          i18next.t('commons.months.september'),
          i18next.t('commons.months.october'),
          i18next.t('commons.months.november'),
          i18next.t('commons.months.december'),
        ],
        shortMonths: [
          i18next.t('commons.short_months.jan'),
          i18next.t('commons.short_months.feb'),
          i18next.t('commons.short_months.mar'),
          i18next.t('commons.short_months.apr'),
          i18next.t('commons.short_months.may'),
          i18next.t('commons.short_months.jun'),
          i18next.t('commons.short_months.jul'),
          i18next.t('commons.short_months.aug'),
          i18next.t('commons.short_months.sep'),
          i18next.t('commons.short_months.oct'),
          i18next.t('commons.short_months.nov'),
          i18next.t('commons.short_months.dec'),
        ],
        weekdays: [
          i18next.t('commons.weekdays.sunday'),
          i18next.t('commons.weekdays.monday'),
          i18next.t('commons.weekdays.tuesday'),
          i18next.t('commons.weekdays.wednesday'),
          i18next.t('commons.weekdays.thursday'),
          i18next.t('commons.weekdays.friday'),
          i18next.t('commons.weekdays.saturday'),
        ],
        contextButtonTitle: i18next.t('dashboard.context_button_title'),
        downloadCSV: i18next.t('dashboard.download_csv'),
        downloadJPEG: i18next.t('dashboard.download_jpeg'),
        downloadPDF: i18next.t('dashboard.download_pdf'),
        downloadPNG: i18next.t('dashboard.download_png'),
        downloadSVG: i18next.t('dashboard.download_svg'),
        downloadXLS: i18next.t('dashboard.download_xls'),
        exitFullscreen: i18next.t('dashboard.exit_full_screen'),
        printChart: i18next.t('dashboard.print_chart'),
        viewFullscreen: i18next.t('dashboard.view_full_screen'),
        viewData: i18next.t('dashboard.view_data'),
      },
    },
  },
  // DARK MODE
  DARK: {
    THEME: {
      components: {
        // style des inputs outlined
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: outlinedBorderColorDark,
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#000000',
            },
          },
        },
        MuiCardActionArea: {
          styleOverrides: {
            root: {
              backgroundColor: '#333333',
            },
          },
        },
        // style des labels des inputs
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: '#F2F2F2',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'unset',
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              backgroundColor: '#334155',
            },
          },
        },
        MUIDataTableToolbar: {
          styleOverrides: {
            root: {
              '@media (max-width: 1200px)': {
                backgroundColor: backgroundDark,
              },
            },
          },
        },

        MUIDataTableFooter: {
          styleOverrides: {
            root: {
              '@media (max-width: 1200px)': {
                backgroundColor: backgroundDark,
              },
            },
          },
        },
        // Style des boutons
        MuiButton: {
          styleOverrides: {
            root: {
              '&.MuiButtonGroup-groupedOutlined.Mui-disabled': {
                border: '1px solid rgba(255, 255, 255, 0.26)',
              },
              '&.MuiButtonGroup-grouped:not(:last-of-type):not(.Mui-disabled):not(.MuiButton-outlined)': {
                borderColor: 'black',
              },
            },
          },
        },
        MuiFab: {
          styleOverrides: {
            root: {
              boxShadow: 'unset',
              '&:active': {
                boxShadow: 'unset',
              },
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              color: '#40ebff',
            },
          },
        },
        // Style des chips
        MuiChip: {
          variants: [
            {
              style: {
                backgroundColor: `${primaryDark}80`,
              },
            },
            {
              style: {
                backgroundColor: `${secondaryDark}80`,
              },
            },
          ],
        },
      },
      palette: {
        mode: 'dark',
        primary: {
          main: primaryDark,
          transparent: `${primaryDark}20`,
          attenuated: `${primaryDark}80`,
        },
        header: {
          main: backgroundHeaderDark,
          menuSelected: backgroundHeaderDark,
        },
        backgroundLogin: {
          main: backgroundLoginDark,
          light: lighten(backgroundLoginDark, 0.2),
          contrastText: getContrastRatio(backgroundLoginDark, '#fff') > 3 ? '#fff' : '#000',
        },
        text: {
          primary: '#e8e8e8',
          neutral: 'rgba(255, 255, 255, 0.54)',
          disabled: 'rgba(255, 255, 255, 0.38)',
          error: '#f44336',
          success: '#81c784',
        },
        secondary: {
          main: secondaryDark,
        },
        background: {
          paper: backgroundDark,
          default: backgroundDark,
          special: backgroundDarkSpecial,
        },
        border: {
          main: outlinedBorderColorDark,
        },
        // Couleurs utiles aux éléments associés aux réseaux sociaux
        dailymotion: {
          main: '#0166cc',
        },
        facebook: {
          main: '#1877f2',
        },
        linkedin: {
          main: '#0a66c2',
        },
        twitter: {
          main: '#55acee',
        },
        whatsapp: {
          main: '#3fc252',
        },
        youtube: {
          main: '#ef462c',
        },
      },
      custom: {
        // style des mots highlités dans le search
        highlightKeyword: {
          color: primaryDark,
        },
      },
    },
    GLOBAL_STYLES: {
      ':root': {
        '--ck-color-toolbar-background': backgroundDark,
        '--ck-color-list-background': backgroundDark,
        '--ck-color-dropdown-panel-background': backgroundDark,
        '--ck-color-list-button-on-text': backgroundDark,
        '--ck-color-base-text': '#ccc',
        '--ck-color-button-on-color': '#dbecff',
        '--ck-color-button-on-background': '#4d4d4d',
        '--ck-color-button-default-hover-background': '#3a3a3a',
        '--ck-color-button-default-active-background': '#3a3a3a',
        '--ck-color-button-on-hover-background': '#2977ff',
        '--ck-color-button-on-active-background': '#2977ff',
      },
      'rect.highcharts-button-box': {
        strokeWidth: 0,
      },
      '.highcharts-contextmenu .highcharts-menu-item:hover': {
        color: '#000000!important', // beurk mais pas trouvé d'alternative...
      },
      '.highcharts-map-navigation .highcharts-button-symbol': {
        stroke: 'rgb(255, 255, 255)',
      },
      '.react-grid-item > .react-resizable-handle.react-resizable-handle-se::after': {
        borderRight: '2px solid rgba(255, 255, 255, 0.7)',
        borderBottom: '2px solid rgba(255, 255, 255, 0.7)',
      },
      '.react-grid-item.react-draggable-dragging .MuiPaper-root, .react-grid-item.resizing .MuiPaper-root': {
        border: '1px solid #e8e8e8',
      },
      '& .ck-editor__main': {
        color: '#000',
      },
      '& .ck-editor__main a': {
        color: '#0000EE',
      },
      // bordure autour des fieldsets des formulaires
      '.formFieldset': {
        borderColor: outlinedBorderColorDark,
      },
      '[data-rmiz-modal-overlay="visible"]': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      },
      blockquote: {
        borderLeft: '5px solid #ccc',
      },
      '.stickyHeader': {
        backgroundColor: backgroundDark,
      },
      '.withBackgroundSpecial': {
        backgroundColor: backgroundDarkSpecial,
      },
      '.dashboard-footer': {
        backgroundColor: '#334155',
      },
      '.bottomBtnsContainer': {
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: backgroundDark,
      },
      '.notistack-MuiContent .MuiButton-root': {
        color: 'white',
        minWidth: 'auto',
      },
      'input[readonly]': {
        color: 'rgba(255, 255, 255, 0.38)',
      },
      // Style des tags dans le html des documents
      // Sélecteur '.html-with-tags > .html-tag' pour n'afficher que les annotations les plus "hautes"
      // Sélecteur '.html-tag:not(:has(span))' pour n'afficher que les annotations les plus "profondes"
      '.html-with-tags > .html-tag': {
        backgroundColor: 'rgb(110, 110, 110)',
      },
    },
    HIGHCHARTS: {
      // useful palettes website : https://coolors.co/palettes/popular/orange,gradient
      colors: [
        '#527D93',
        '#56A0A1',
        '#56C2B5',
        '#A8C9A0',
        '#DBDF8E',
        '#F4D18A',
        '#F4CB8A',
        '#F6B88B',
        '#F2A37D',
        '#F19478',
      ],
      speakersColors: [
        '#31A6F3',
        '#F59C9C',
        '#2C5F5B',
        '#D56732',
        '#0496A2',
        '#B41A84',
        '#046084',
        '#E0D53B',
        '#783B09',
        '#7F62B5',
      ],
      entitiesColors: [
        '#F19C9C',
        '#5CBF50',
        '#31A6F3',
        '#FF6752',
        '#049698',
        '#B41A59',
        '#046084',
        '#E0D53B',
        '#FF9737',
        '#7F62B5',
      ],
      relationsEntitiesColors: [
        '#006064',
        '#E91E63',
        '#ff7777',
        '#2196F3',
        '#52a5af',
        '#6a66cc',
        '#cb5d9e',
        '#4CAF50',
        '#FFC107',
        '#7893f5',
        '#b3781b',
        '#2f590a',
        '#ff0000',
        '#00ff00',
        '#0000ff',
      ],
      chart: {
        backgroundColor: backgroundDark,
        style: {
          color: '#ffffff',
        },
      },
      title: {
        style: {
          fontWeight: 'normal',
          color: '#ffffff',
        },
      },
      legend: {
        align: 'right',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#c8c8c8',
        },
        itemHoverStyle: {
          color: '#ffffff',
        },
      },
      xAxis: {
        gridLineColor: '#dddddd',
        lineColor: '#dddddd',
        minorGridLineColor: '#dddddd',
        tickColor: '#dddddd',
        labels: {
          style: {
            color: '#ffffff',
          },
        },
      },
      yAxis: {
        gridLineColor: '#dddddd',
        lineColor: '#dddddd',
        minorGridLineColor: '#dddddd',
        tickColor: '#dddddd',
        labels: {
          style: {
            color: '#ffffff',
          },
        },
      },
      mapNavigation: {
        buttonOptions: {
          theme: {
            fill: backgroundDark,
            stroke: '#aaaaaa',
            style: {
              color: '#ffffff',
            },
            states: {
              hover: {
                fill: '#666666',
              },
            },
          },
        },
      },
      navigation: {
        menuStyle: {
          background: backgroundDark,
        },
        menuItemStyle: {
          color: '#ffffff',
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            symbolStroke: '#ffffff',
            theme: {
              fill: backgroundDark,
              stroke: '#ffffff',
              states: {
                hover: {
                  fill: '#666666',
                },
                select: {
                  fill: '#666666',
                },
              },
            },
          },
        },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            color: '#ffffff',
            connectorColor: '#e8e8e8',
          },
        },
        column: {
          borderWidth: 0,
        },
        bar: {
          borderWidth: 0,
        },
        sunburst: {
          borderWidth: 0,
        },
        treemap: {
          borderWidth: 0,
        },
      },
    },
  },
};

if (CUSTOM_PATH) {
  const isDev = (process.env.NODE_ENV === 'development');
  try {
    // eslint-disable-next-line import/no-dynamic-require, global-require
    const { default: CUSTOM_THEME_CONFIG } = require(`custom/${CUSTOM_PATH}/core/theme`);
    if (isDev) {
      console.info(`CUSTOM PATH LOADED : custom/${CUSTOM_PATH}/core/theme`);
    }
    THEME = _.merge(THEME, CUSTOM_THEME_CONFIG);
  } catch (err) {
    _.noop();
  }
}

const THEME_CONFIG = THEME;
export default THEME_CONFIG;
